import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { 
    Megaphone, Plus, Trash2, Users, FileText, 
    CheckCircle2, XCircle, ChevronLeft, Search, 
    PenTool, X, AlertTriangle, Building2, Download
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const ToolboxTalks = () => {
    const [talks, setTalks] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isOwner, setIsOwner] = useState(false);
    
    // UI Modes & Responsive
    const [viewMode, setViewMode] = useState('list'); // 'list', 'create', 'register'
    const [selectedTalk, setSelectedTalk] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Create Form State
    const [newTalk, setNewTalk] = useState({ title: '', description: '', assigned_companies: [], file: null, contractor_led: false });

    useEffect(() => {
        // Mobile Listener
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        
        loadData();
        checkRole();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const checkRole = async () => {
        try {
            const res = await api.get('users/me/');
            setIsOwner(res.data.is_superuser || res.data.is_staff);
        } catch (e) { console.error(e); }
    };

    const loadData = async () => {
        try {
            const [tRes, cRes] = await Promise.all([
                api.get('toolbox/'), 
                api.get('companies/')
            ]);
            setTalks(tRes.data);
            setCompanies(cRes.data.filter(c => c.type !== 'OWNER')); 
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    // --- 1. CREATION LOGIC ---
    const handleCreate = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('title', newTalk.title);
        formData.append('description', newTalk.description);
        formData.append('contractor_led', String(newTalk.contractor_led));

        newTalk.assigned_companies.forEach(id => formData.append('assigned_companies', id));
        if (newTalk.file) formData.append('attachment', newTalk.file);

        try {
            await api.post('toolbox/', formData);
            alert("Talk Created Successfully");
            setViewMode('list');
            setNewTalk({ title: '', description: '', assigned_companies: [], file: null, contractor_led: false });
            loadData();
        } catch (err) { alert("Error creating talk"); }
    };

    const toggleCompany = (id) => {
        setNewTalk(prev => {
            const current = prev.assigned_companies;
            return current.includes(id) ? { ...prev, assigned_companies: current.filter(c => c !== id) } : { ...prev, assigned_companies: [...current, id] };
        });
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if(!window.confirm("Delete this toolbox talk?")) return;
        try { await api.delete(`toolbox/${id}/`); setTalks(prev => prev.filter(t => t.id !== id)); } 
        catch(err) { alert("Error deleting"); }
    };

    // --- 2. OPEN REGISTER LOGIC ---
    const handleOpenRegister = async (talk) => {
        try {
            setLoading(true);
            const res = await api.get(`toolbox/${talk.id}/register/`); 
            setSelectedTalk({ ...talk, roster: res.data });
            setViewMode('register');
        } catch (err) { alert("Error loading register - Check Access"); }
        finally { setLoading(false); }
    };

    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', boxSizing:'border-box', marginBottom:'10px' };

    // --- CONDITIONAL RENDERING FOR REGISTER ---
    if (viewMode === 'register' && selectedTalk) {
        return <ToolboxRegister talk={selectedTalk} onBack={() => { setSelectedTalk(null); setViewMode('list'); }} isMobile={isMobile} />;
    }

    if (loading) return <div style={{color:'white'}}>Loading Talks...</div>;

    return (
        <div className="animate-fade-in">
            {/* HEADER */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'25px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <Megaphone size={28} color={THEME.primary} />
                    <div>
                        <h2 style={{color: 'white', margin:0}}>Toolbox Talks</h2>
                        <p style={{color: THEME.textSec, margin:'5px 0 0 0', fontSize:'0.9rem'}}>
                            {viewMode === 'list' ? 'Manage and assign safety briefings.' : 'Create new briefing content.'}
                        </p>
                    </div>
                </div>
                <div>
                    {viewMode === 'list' && isOwner && (
                        <button onClick={() => setViewMode('create')} style={{padding:'10px 20px', background: THEME.primary, color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px'}}>
                            <Plus size={18}/> Create New Talk
                        </button>
                    )}
                    {viewMode === 'create' && (
                        <button onClick={() => setViewMode('list')} style={{padding:'10px 20px', background: 'transparent', color: THEME.textSec, border:`1px solid ${THEME.border}`, borderRadius:'8px', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px'}}>
                            <ChevronLeft size={18}/> Back to List
                        </button>
                    )}
                </div>
            </div>

            {/* 1. LIST VIEW */}
            {viewMode === 'list' && (
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap:'20px'}}>
                    {talks.map(t => (
                        <div key={t.id} style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, padding:'20px', display:'flex', flexDirection:'column', position:'relative'}}>
                            {/* TOP ROW: Title & Delete */}
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'start', marginBottom:'10px'}}>
                                <div>
                                    {t.contractor_led ? (
                                        <span style={{fontSize:'0.7rem', color: THEME.warning, border:`1px solid ${THEME.warning}`, padding:'2px 6px', borderRadius:'4px', fontWeight:'bold', display:'inline-flex', alignItems:'center', gap:'4px', marginBottom:'5px'}}><AlertTriangle size={10}/> Contractor Led</span>
                                    ) : (
                                        <span style={{fontSize:'0.7rem', color: THEME.primary, border:`1px solid ${THEME.primary}`, padding:'2px 6px', borderRadius:'4px', fontWeight:'bold', display:'inline-flex', alignItems:'center', gap:'4px', marginBottom:'5px'}}><Building2 size={10}/> Owner Led</span>
                                    )}
                                    <h4 style={{margin:0, color:'white', fontSize:'1.1rem'}}>{t.title}</h4>
                                </div>
                                {isOwner && <button onClick={(e) => handleDelete(t.id, e)} style={{background:'transparent', border:'none', cursor:'pointer', color:THEME.textSec}}><Trash2 size={18}/></button>}
                            </div>
                            
                            <div style={{fontSize:'0.85rem', color: THEME.textSec, marginBottom:'15px', whiteSpace:'pre-wrap', flex:1, maxHeight:'60px', overflow:'hidden', textOverflow:'ellipsis'}}>
                                {t.description}
                            </div>
                            
                            {t.attachment && (
                                <div style={{marginBottom:'15px'}}>
                                    <a href={t.attachment} target="_blank" rel="noopener noreferrer" style={{color:THEME.primary, textDecoration:'none', fontSize:'0.85rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:'6px'}}>
                                        <Download size={14}/> Attachment Available
                                    </a>
                                </div>
                            )}

                            <div style={{borderTop:`1px solid ${THEME.border}`, paddingTop:'15px', marginTop:'auto'}}>
                                <div style={{fontSize:'0.75rem', color: THEME.textSec, marginBottom:'5px', textTransform:'uppercase'}}>Assigned To:</div>
                                <div style={{display:'flex', gap:'5px', flexWrap:'wrap', marginBottom:'15px'}}>
                                    {t.assigned_companies_names && t.assigned_companies_names.length > 0 ? 
                                        t.assigned_companies_names.map((name, i) => (
                                            <span key={i} style={{fontSize:'0.75rem', background:'rgba(255, 255, 255, 0.05)', color: 'white', padding:'2px 8px', borderRadius:'4px'}}>{name}</span>
                                        )) : <span style={{color: THEME.success, fontSize:'0.8rem'}}>All Site Personnel</span>
                                    }
                                </div>
                                <button onClick={() => handleOpenRegister(t)} style={{width:'100%', padding:'10px', background: THEME.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}>
                                    <Users size={16}/> View / Sign Register
                                </button>
                            </div>
                        </div>
                    ))}
                    {talks.length === 0 && <div style={{textAlign:'center', color:THEME.textSec, marginTop:'40px', gridColumn:'1/-1'}}>No talks scheduled.</div>}
                </div>
            )}

            {/* 2. CREATE VIEW (Only if Owner) */}
            {viewMode === 'create' && isOwner && (
                <div style={{background: THEME.card, padding: isMobile ? '15px' : '30px', borderRadius:'12px', border:`1px solid ${THEME.primary}`, maxWidth:'800px', margin:'0 auto'}}>
                    <form onSubmit={handleCreate}>
                        <label style={{display:'block', color:THEME.textSec, fontSize:'0.9rem', marginBottom:'5px'}}>Talk Title</label>
                        <input placeholder="e.g. Working at Height" value={newTalk.title} onChange={e => setNewTalk({...newTalk, title: e.target.value})} style={inputStyle} required />
                        
                        <label style={{display:'block', color:THEME.textSec, fontSize:'0.9rem', marginBottom:'5px'}}>Content / Agenda</label>
                        <textarea rows="5" placeholder="Key points to discuss..." value={newTalk.description} onChange={e => setNewTalk({...newTalk, description: e.target.value})} style={inputStyle} required />
                        
                        <label style={{display:'block', color:THEME.textSec, fontSize:'0.9rem', marginBottom:'8px'}}>Assign Companies (Optional - Leave blank for ALL):</label>
                        <div style={{display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'20px', maxHeight:'150px', overflowY:'auto'}}>
                            {companies.map(c => (
                                <div key={c.id} onClick={() => toggleCompany(c.id)} style={{
                                    padding:'8px 16px', borderRadius:'20px', fontSize:'0.85rem', cursor:'pointer', border: newTalk.assigned_companies.includes(c.id) ? `1px solid ${THEME.primary}` : `1px solid ${THEME.border}`,
                                    background: newTalk.assigned_companies.includes(c.id) ? 'rgba(59, 130, 246, 0.2)' : 'transparent', color: newTalk.assigned_companies.includes(c.id) ? THEME.primary : THEME.textSec,
                                    display: 'flex', alignItems: 'center', gap: '6px'
                                }}>
                                    {c.name} {newTalk.assigned_companies.includes(c.id) && <CheckCircle2 size={12}/>}
                                </div>
                            ))}
                        </div>

                        <div style={{background:'#0f172a', padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.warning}`, marginBottom:'20px'}}>
                            <label style={{display:'flex', alignItems:'center', cursor:'pointer', color:'white', fontWeight:'bold'}}>
                                <input type="checkbox" checked={newTalk.contractor_led} onChange={e => setNewTalk({...newTalk, contractor_led: e.target.checked})} style={{marginRight:'10px', width:'20px', height:'20px', accentColor: THEME.warning}} />
                                Allow Contractor Supervisors to Lead this Talk?
                            </label>
                            <div style={{color: THEME.textSec, fontSize:'0.85rem', marginLeft:'30px', marginTop:'5px'}}>If checked, assigned contractors can open this talk and conduct it for their own teams.</div>
                        </div>

                        <label style={{display:'block', color:THEME.textSec, fontSize:'0.9rem', marginBottom:'5px'}}>Attachment (PDF/Image)</label>
                        <input type="file" onChange={e => setNewTalk({...newTalk, file: e.target.files[0]})} style={{...inputStyle, padding:'6px'}} />

                        <button type="submit" style={{width:'100%', padding:'12px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer', fontSize:'1rem'}}>Publish Talk</button>
                    </form>
                </div>
            )}
        </div>
    );
};

// --- SUB-COMPONENT: REGISTER VIEW ---
const ToolboxRegister = ({ talk, onBack, isMobile }) => {
    const [roster, setRoster] = useState(talk.roster || []);
    const [filterCompany, setFilterCompany] = useState('');
    const [search, setSearch] = useState('');
    
    // Modal & Signature State
    const [signingPerson, setSigningPerson] = useState(null);
    const [signDate, setSignDate] = useState(new Date().toISOString().split('T')[0]);
    const [viewingSignature, setViewingSignature] = useState(null);
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSigned, setHasSigned] = useState(false);

    // Drawing Logic
    const startDrawing = (e) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        ctx.beginPath();
        ctx.moveTo(clientX - rect.left, clientY - rect.top);
        setIsDrawing(true);
        setHasSigned(true);
    };
    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        ctx.lineTo(clientX - rect.left, clientY - rect.top);
        ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.stroke();
    };
    const stopDrawing = () => setIsDrawing(false);
    const clearSignature = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setHasSigned(false);
    };

    const handleSave = async () => {
        if (!signingPerson) return;
        let sigData = null;
        if (signDate && hasSigned && canvasRef.current) sigData = canvasRef.current.toDataURL('image/png');

        try {
            await api.post(`toolbox/${talk.id}/register/`, {
                db_type: signingPerson.db_type, id: signingPerson.id,
                date: signDate || null, signature: sigData
            });
            alert("Signed off Successfully");
            setRoster(prev => prev.map(p => {
                if (p.id === signingPerson.id && p.db_type === signingPerson.db_type) {
                    return { ...p, is_completed: true, completion_date: signDate, signature: sigData, conducted_by: 'Me' };
                }
                return p;
            }));
            setSigningPerson(null); setHasSigned(false);
        } catch (err) { alert("Error saving - Are you allowed to sign this?"); }
    };

    const filteredRoster = roster.filter(p => {
        const matchesName = p.name.toLowerCase().includes(search.toLowerCase());
        const matchesComp = filterCompany ? p.company === filterCompany : true;
        return matchesName && matchesComp;
    });
    const uniqueCompanies = [...new Set(roster.map(p => p.company))];

    return (
        <div className="animate-fade-in">
            {/* Header */}
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'15px', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center'}}>
                    <button onClick={onBack} style={{background:'transparent', border:`1px solid ${THEME.border}`, color:THEME.textSec, padding:'8px 15px', borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}><ChevronLeft size={16}/> Back</button>
                    <div>
                        <h2 style={{color:'white', margin:0, fontSize: isMobile ? '1.2rem' : '1.5rem'}}>Register: {talk.title}</h2>
                        {talk.attachment && <a href={talk.attachment} target="_blank" rel="noreferrer" style={{color:'#3b82f6', textDecoration:'none', fontSize:'0.9rem', marginTop:'5px', display:'flex', alignItems:'center', gap:'4px'}}><FileText size={14}/> View Presentation Materials</a>}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div style={{display:'flex', gap:'15px', marginBottom:'20px', flexWrap:'wrap', flexDirection: isMobile ? 'column' : 'row'}}>
                <div style={{position:'relative', width: isMobile ? '100%' : 'auto'}}>
                    <Search size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/>
                    <input type="text" placeholder="Search Name..." value={search} onChange={e => setSearch(e.target.value)} style={{padding:'10px 10px 10px 35px', borderRadius:'5px', background:'#1e293b', border:`1px solid ${THEME.border}`, color:'white', minWidth:'200px', boxSizing:'border-box', width:'100%'}} />
                </div>
                <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={{padding:'10px', borderRadius:'5px', background:'#1e293b', border:`1px solid ${THEME.border}`, color:'white', minWidth:'200px'}}>
                    <option value="">All Companies</option>{uniqueCompanies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div style={{marginLeft: isMobile ? '0' : 'auto', display:'flex', gap:'15px', color:'#94a3b8', alignItems:'center'}}>
                    <span>Total: <strong style={{color:'white'}}>{roster.length}</strong></span>
                    <span>Completed: <strong style={{color:'#4ade80'}}>{roster.filter(p => p.is_completed).length}</strong></span>
                </div>
            </div>

            {/* Table Scroll Wrapper */}
            <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
                <div style={{overflowX: 'auto'}}>
                    <table style={{width:'100%', minWidth: '600px', borderCollapse:'collapse', textAlign:'left', color:'#cbd5e1'}}>
                        <thead style={{background:'#0f172a', color:'#94a3b8', textTransform:'uppercase', fontSize:'0.85rem'}}>
                            <tr><th style={{padding:'15px'}}>Name</th><th>Company</th><th>Role</th><th>Status</th><th>Date</th><th>Signature</th><th style={{textAlign:'right', paddingRight:'15px'}}>Action</th></tr>
                        </thead>
                        <tbody>
                            {filteredRoster.map((p, idx) => (
                                <tr key={`${p.db_type}-${p.id}`} style={{borderBottom:`1px solid ${THEME.border}`, background: idx%2===0?'transparent':'rgba(255,255,255,0.02)'}}>
                                    <td style={{padding:'12px 15px', fontWeight:'bold', color:'white'}}>{p.name}</td>
                                    <td>{p.company}</td>
                                    <td>{p.role}</td>
                                    <td>{p.is_completed ? <span style={{color:'#4ade80', display:'flex', alignItems:'center', gap:'4px'}}><CheckCircle2 size={14}/> Done</span> : <span style={{color:'#f87171', display:'flex', alignItems:'center', gap:'4px'}}><XCircle size={14}/> Pending</span>}</td>
                                    <td>{p.completion_date || '-'}</td>
                                    <td>{p.signature ? <div onClick={() => setViewingSignature(p.signature)} style={{width:'80px', height:'30px', background:'white', cursor:'pointer', border:`1px solid ${THEME.border}`, display:'flex', alignItems:'center', justifyContent:'center'}}><img src={p.signature} alt="Sig" style={{maxHeight:'100%', maxWidth:'100%'}} /></div> : '-'}</td>
                                    <td style={{textAlign:'right', paddingRight:'15px'}}>
                                        <button onClick={() => { setSigningPerson(p); setSignDate(p.completion_date || new Date().toISOString().split('T')[0]); }} style={{padding:'5px 10px', fontSize:'0.8rem', background:'transparent', border:`1px solid ${THEME.primary}`, color: THEME.primary, borderRadius:'6px', cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'4px'}}>
                                            <PenTool size={12}/> Sign Off
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Signature Modal - MOBILE RESPONSIVE */}
            {signingPerson && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}>
                    <div style={{background: THEME.card, padding:'30px', borderRadius:'12px', border:`1px solid ${THEME.primary}`, width: isMobile ? '90%' : '400px', boxShadow:'0 0 25px rgba(59, 130, 246, 0.3)'}}>
                        <h3 style={{marginTop:0, color:'white'}}>Sign Off: {signingPerson.name}</h3>
                        <p style={{color:'#94a3b8'}}>I confirm I have attended the toolbox talk: <strong>{talk.title}</strong></p>
                        <input type="date" value={signDate} onChange={e => setSignDate(e.target.value)} style={{width:'100%', padding:'10px', marginBottom:'15px', borderRadius:'4px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', boxSizing:'border-box'}} />
                        {signDate && (
                            <div style={{marginBottom:'20px'}}>
                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'5px'}}>
                                    <label style={{color: THEME.textSec, fontSize:'0.9rem'}}>Signature:</label>
                                    <button onClick={clearSignature} style={{fontSize:'0.75rem', background:'transparent', border:'none', color:'#f87171', cursor:'pointer', display:'flex', alignItems:'center', gap:'4px'}}><Trash2 size={12}/> Clear</button>
                                </div>
                                {/* Touch Action None is CRITICAL for mobile drawing */}
                                <div style={{background:'white', borderRadius:'4px', overflow:'hidden', touchAction: 'none'}}>
                                    <canvas ref={canvasRef} width={isMobile ? 300 : 360} height={150} style={{display:'block', cursor:'crosshair'}}
                                        onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                                        onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing}
                                    />
                                </div>
                            </div>
                        )}
                        <div style={{display:'flex', justifyContent:'flex-end', gap:'10px'}}>
                            <button onClick={() => setSigningPerson(null)} style={{background:'transparent', border:`1px solid ${THEME.border}`, color:THEME.textSec, padding:'10px 20px', borderRadius:'6px', cursor:'pointer'}}>Cancel</button>
                            <button onClick={handleSave} disabled={!hasSigned && !signingPerson.is_completed} style={{background: (!hasSigned && !signingPerson.is_completed) ? '#334155' : THEME.success, color:'white', border:'none', borderRadius:'6px', padding:'10px 20px', cursor: (!hasSigned && !signingPerson.is_completed) ? 'not-allowed' : 'pointer', fontWeight:'bold'}}>Save</button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* View Signature Modal */}
            {viewingSignature && (
                <div onClick={() => setViewingSignature(null)} style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100}}>
                    <div style={{background:'white', padding:'10px', borderRadius:'8px'}} onClick={e => e.stopPropagation()}>
                        <img src={viewingSignature} alt="Signature" style={{maxWidth:'500px', maxHeight:'300px'}} />
                        <button onClick={() => setViewingSignature(null)} style={{display:'block', width:'100%', marginTop:'10px', padding:'10px', background:'#ef4444', color:'white', border:'none', borderRadius:'4px', cursor:'pointer'}}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ToolboxTalks;